import { useCallback } from "react";
import { useLocation, useMatch } from "react-router";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { fetchWithTimeout } from "../misc/fetchWithTimeout";
import {
  BC_ENVIRONMENT_STORE_KEY,
  DEFAULT_BC_ENVIRONMENT,
  isBcEnvironmentCode,
} from "../misc/bcEnvironment";
import { crmPreferencesStore } from "../settings/userPreferences";
import type { VoiceAgentPayload, VoiceAgentResponse } from "./types";

type GetUserFn = () => Promise<{
  data: { user: { id: string } | null };
}>;

const WEBHOOK_TIMEOUT_MS = 300_000; // 5 minutes

function resolveContext(
  pathname: string,
  matches: Record<string, string | null>,
): { page: string; recordId: string | null } {
  const segments = pathname.split("/").filter(Boolean);
  const page = segments[0] ?? "dashboard";
  const recordId = matches[page] ?? null;
  return { page, recordId };
}

function resolveBcEnvironmentFallback(): string {
  const stored = crmPreferencesStore.getItem<string>(BC_ENVIRONMENT_STORE_KEY);
  return isBcEnvironmentCode(stored) ? stored : DEFAULT_BC_ENVIRONMENT;
}

export function useAgentWebhook(options?: { getUser?: GetUserFn }) {
  const getUser =
    options?.getUser ?? (() => getSupabaseClient().auth.getUser());

  const location = useLocation();
  const contactsMatch = useMatch("/contacts/:id/*");
  const companiesMatch = useMatch("/companies/:id/*");
  const dealsMatch = useMatch("/deals/:id/*");
  const tasksMatch = useMatch("/tasks/:id/*");

  const submit = useCallback(
    async ({
      text,
      sessionId,
      language,
    }: {
      text: string;
      sessionId: string;
      language: string;
    }): Promise<string> => {
      const recordIdMatches: Record<string, string | null> = {
        contacts: contactsMatch?.params.id ?? null,
        companies: companiesMatch?.params.id ?? null,
        deals: dealsMatch?.params.id ?? null,
        tasks: tasksMatch?.params.id ?? null,
      };
      const { page, recordId } = resolveContext(
        location.pathname,
        recordIdMatches,
      );

      const {
        data: { user },
      } = await getUser();

      const payload: VoiceAgentPayload = {
        text,
        page,
        recordId,
        userId: user?.id ?? "",
        timestamp: new Date().toISOString(),
        sessionId,
        language,
        bcEnvironment: resolveBcEnvironmentFallback(),
      };

      const res = await fetchWithTimeout(
        import.meta.env.VITE_N8N_WEBHOOK_URL as string,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          timeout: WEBHOOK_TIMEOUT_MS,
        },
      );

      if (!res.ok) {
        throw new Error(`Webhook returned ${res.status}`);
      }

      const data: VoiceAgentResponse = await res.json();
      const message =
        data.message ??
        (data as any).output ??
        (typeof data === "string" ? data : null);
      return message;
    },
    [location.pathname, contactsMatch, companiesMatch, dealsMatch, tasksMatch], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return { submit };
}
